import { isPlainObject } from "lodash-es";

import { ManifestJSONSchema } from "src/api/connectApi/manifestTypes";
import {
  CustomValidator,
  createDefaultAjv,
  noAllOf,
  noInternalSchemaDefs,
  noRealUnions,
  ValidationError,
  validateJsonSchema,
} from "src/jsonSchema/validateJsonSchema";

/** Error if any object contains another object (directly or inside array items). */

const findNestedObject = (
  props: Record<string, unknown>,
  parentPath: string,
  nested: boolean,
): { type: "custom"; message: string } | null => {
  for (const [name, value] of Object.entries(props)) {
    if (!isPlainObject(value)) continue;
    const node = value as Record<string, unknown>;
    const path = parentPath ? `${parentPath}.${name}` : name;

    // Collect the inner properties — either directly or via items
    const childProps = node.properties;
    const itemProps = isPlainObject(node.items)
      ? (node.items as Record<string, unknown>).properties
      : undefined;
    const innerProps = isPlainObject(childProps)
      ? childProps
      : isPlainObject(itemProps)
        ? itemProps
        : undefined;

    if (!innerProps) continue;

    if (nested) {
      return {
        type: "custom",
        message: `Property '${path}' has nested object structure. Node inputs only support one level of nesting. Please flatten the schema.`,
      };
    }

    const error = findNestedObject(
      innerProps as Record<string, unknown>,
      path,
      true,
    );
    if (error) return error;
  }
  return null;
};

const noDeeplyNestedObjects: CustomValidator = (schema) => {
  const props = (schema as Record<string, unknown>).properties;
  if (!props || typeof props !== "object" || Array.isArray(props)) return null;
  return findNestedObject(props as Record<string, unknown>, "", false);
};

type SchemaNode = Record<string, unknown>;

const SNAKE_CASE_PROPERTY_RE = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/;

const innerObjectSchema = (propSchema: SchemaNode): SchemaNode | null => {
  if (isPlainObject(propSchema.properties)) return propSchema;
  if (
    isPlainObject(propSchema.items) &&
    isPlainObject((propSchema.items as SchemaNode).properties)
  ) {
    return propSchema.items as SchemaNode;
  }
  return null;
};

const typeIncludesNull = (propSchema: SchemaNode): boolean => {
  const propType = propSchema.type;
  if (propType === "null") return true;
  if (Array.isArray(propType) && propType.includes("null")) return true;
  const unionMembers = [
    ...(Array.isArray(propSchema.oneOf) ? propSchema.oneOf : []),
    ...(Array.isArray(propSchema.anyOf) ? propSchema.anyOf : []),
  ];
  return unionMembers.some(
    (member) => isPlainObject(member) && (member as SchemaNode).type === "null",
  );
};

const validateNodeInputProperties = (
  inputSchema: SchemaNode,
  parentPath = "",
): ValidationError | null => {
  const properties = inputSchema.properties;
  if (!isPlainObject(properties) || Array.isArray(properties)) return null;
  const props = properties as Record<string, unknown>;
  const required = new Set(
    Array.isArray(inputSchema.required) ? inputSchema.required : [],
  );

  return Object.entries(props).reduce<ValidationError | null>(
    (found, [name, propSchema]) => {
      if (found) return found;
      const path = parentPath ? `${parentPath}.${name}` : name;
      if (!SNAKE_CASE_PROPERTY_RE.test(name)) {
        return {
          type: "custom",
          message: `Property '${path}' must be snake_case`,
        };
      }
      if (!isPlainObject(propSchema)) return found;
      const node = propSchema as SchemaNode;

      const title = node.title;
      if (typeof title !== "string" || !title.trim()) {
        return {
          type: "custom",
          message: `Property '${path}' must have a non-empty title`,
        };
      }

      const isRefOnly =
        typeof node.$ref === "string" && node.type === undefined;
      if (!isRefOnly && !required.has(name) && !typeIncludesNull(node)) {
        return {
          type: "custom",
          message: `Property '${path}' is not required and must include 'null' in type`,
        };
      }

      const innerSchema = innerObjectSchema(node);
      return innerSchema
        ? validateNodeInputProperties(innerSchema, path)
        : found;
    },
    null,
  );
};

export type ParseResult = {
  schema: ManifestJSONSchema | null;
  error: ValidationError | null;
  isValid: boolean;
};

export const parseAndValidateJsonSchema = (jsonString: string): ParseResult => {
  // Create an AJV instance with manifest-specific keywords
  const ajv = createDefaultAjv(true); // Allow union types for validation
  ajv.addKeyword("widget"); // Custom keyword for UI widget hints
  ajv.addKeyword("enumLabels"); // Custom keyword for enum display labels
  ajv.addKeyword("example"); // Custom keyword for example values

  // Step 1: Validate JSON Schema structure using existing utility with custom validators
  const structuralError = validateJsonSchema(jsonString, {
    customValidators: [
      noRealUnions,
      noAllOf,
      noInternalSchemaDefs,
      noDeeplyNestedObjects,
      validateNodeInputProperties,
    ],
    ajvValidator: ajv,
  });

  if (structuralError) {
    return {
      schema: null,
      error: structuralError,
      isValid: false,
    };
  }

  // Step 2: Parse the validated JSON
  let parsed: ManifestJSONSchema;
  try {
    parsed = JSON.parse(jsonString) as ManifestJSONSchema;
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    return {
      schema: null,
      error: {
        type: "json",
        message: `JSON Parse Error: ${message}`,
      },
      isValid: false,
    };
  }

  // Step 3: Basic manifest compatibility check
  // Ensure the schema has properties (node_inputs format expectation)
  if (parsed.type === "object" && !parsed.properties) {
    return {
      schema: null,
      error: {
        type: "structure",
        message:
          "Schema must have 'properties' field when type is 'object' to be compatible with manifest UI",
      },
      isValid: false,
    };
  }

  return {
    schema: parsed,
    error: null,
    isValid: true,
  };
};
